/**
 * Welcome carousel — 3 slides shown on first run after setup.
 */

import React, { useRef, useState, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
  type ViewToken,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from 'react-i18next';
import { useTheme, type ThemeColors } from '../../utils/theme';
import { FONT_FAMILY, FONT_SIZE, SPACING, RADIUS } from '../../utils/design-tokens';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

interface Slide {
  key: string;
  emoji: string;
  titleKey: string;
  subtitleKey: string;
}

const SLIDES: Slide[] = [
  { key: '1', emoji: '🐟', titleKey: 'onboarding.slide1Title', subtitleKey: 'onboarding.slide1Sub' },
  { key: '2', emoji: '💬', titleKey: 'onboarding.slide2Title', subtitleKey: 'onboarding.slide2Sub' },
  { key: '3', emoji: '🔔', titleKey: 'onboarding.slide3Title', subtitleKey: 'onboarding.slide3Sub' },
];

interface Props {
  onDone: () => void;
}

export default function WelcomeCarousel({ onDone }: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const flatListRef = useRef<FlatList>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const onViewableItemsChanged = useCallback(({ viewableItems }: { viewableItems: ViewToken[] }) => {
    if (viewableItems.length > 0 && viewableItems[0].index != null) {
      setActiveIndex(viewableItems[0].index);
    }
  }, []);

  const viewConfig = useRef({ viewAreaCoveragePercentThreshold: 50 }).current;

  const handleNext = () => {
    if (activeIndex < SLIDES.length - 1) {
      flatListRef.current?.scrollToIndex({ index: activeIndex + 1, animated: true });
    } else {
      onDone();
    }
  };

  const renderSlide = ({ item }: { item: Slide }) => (
    <View style={s.slide}>
      <Text style={s.emoji}>{item.emoji}</Text>
      <Text style={s.title}>{t(item.titleKey)}</Text>
      <Text style={s.subtitle}>{t(item.subtitleKey)}</Text>
    </View>
  );

  const isLast = activeIndex === SLIDES.length - 1;

  return (
    <View style={s.container}>
      <FlatList
        ref={flatListRef}
        data={SLIDES}
        renderItem={renderSlide}
        keyExtractor={(item) => item.key}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onViewableItemsChanged={onViewableItemsChanged}
        viewabilityConfig={viewConfig}
        bounces={false}
      />

      {/* Dots */}
      <View style={s.dots}>
        {SLIDES.map((_, i) => (
          <View key={i} style={[s.dot, i === activeIndex && s.dotActive]} />
        ))}
      </View>

      {/* Buttons */}
      <View style={s.buttons}>
        {!isLast && (
          <TouchableOpacity onPress={onDone} style={s.skipBtn} accessibilityRole="button" accessibilityLabel={t('common.skip', 'Skip')}>
            <Text style={s.skipText}>{t('common.skip', 'Skip')}</Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity onPress={handleNext} activeOpacity={0.85} accessibilityRole="button">
          <LinearGradient colors={['#64B5F6', '#1E88E5']} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={s.nextBtn}>
            <Text style={s.nextText}>{isLast ? t('onboarding.getStarted', 'Get Started') : t('onboarding.next', 'Next')}</Text>
          </LinearGradient>
        </TouchableOpacity>
      </View>
    </View>
  );
}

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    slide: {
      width: SCREEN_WIDTH,
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: SPACING['2xl'],
    },
    emoji: {
      fontSize: 72,
      marginBottom: SPACING.xl,
    },
    title: {
      fontFamily: FONT_FAMILY.heading,
      fontSize: FONT_SIZE['2xl'],
      color: colors.text,
      textAlign: 'center',
      marginBottom: SPACING.md,
    },
    subtitle: {
      fontFamily: FONT_FAMILY.body,
      fontSize: FONT_SIZE.base,
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: FONT_SIZE.base * 1.5,
      paddingHorizontal: SPACING.base,
    },
    dots: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: SPACING.xl,
    },
    dot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: colors.border,
      marginHorizontal: 4,
    },
    dotActive: {
      width: 24,
      borderRadius: 4,
      backgroundColor: colors.primary,
    },
    buttons: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: SPACING.xl,
      paddingBottom: SPACING['3xl'],
    },
    skipBtn: {
      paddingVertical: SPACING.md,
      paddingHorizontal: SPACING.base,
    },
    skipText: {
      fontFamily: FONT_FAMILY.bodyMedium,
      fontSize: FONT_SIZE.base,
      color: colors.textSecondary,
    },
    nextBtn: {
      paddingVertical: SPACING.md,
      paddingHorizontal: SPACING['2xl'],
      borderRadius: RADIUS.lg,
      minWidth: 140,
      alignItems: 'center',
    },
    nextText: {
      fontFamily: FONT_FAMILY.bodySemibold,
      fontSize: FONT_SIZE.base,
      color: '#FFFFFF',
    },
  });
}
